import { useState } from 'react';
import type { ReactNode } from 'react';
import { Switch } from 'antd';

import { Button, Dialog, Field, Input, Select } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { LoiTrongHop } from '../../tenant/components/phan-form';
import { useChatTelegram, useLuuKenh } from '../hooks/useCanhBao';
import { useDichCanhBao } from '../hooks/useDichCanhBao';
import { dongGoiCauHinh, giaTriBanDau, kiemForm, oBiAn } from '../nghiep-vu/canh-bao';
import type { GiaTriForm, GiaTriO, LoiTruong } from '../nghiep-vu/canh-bao';
import type { MaKenh, NotifyChannel, NotifyChannelType, NotifyField, PhamViCanhBao, TelegramChat } from '../types';
import { OTruong } from './OTruong';

/** Biến dùng được trong thân webhook tuỳ biến — cú pháp mẫu, không dịch. */
const BIEN_WEBHOOK = ['{{title}}', '{{message}}', '{{type}}', '{{severity}}', '{{occurred_at}}'];

/**
 * Hộp thêm hoặc sửa một cấu hình kênh, form dựng từ `fields` của loại kênh. Lỗi của server hiện trong hộp, hộp không
 * đóng; `412` là có người vừa sửa trước: danh sách đã tải lại, đóng hộp rồi mở lại để sửa trên bản mới.
 */
export function HopKenh({
  pv,
  loaiKenh,
  dangCo,
  onClose,
}: {
  pv: PhamViCanhBao;
  loaiKenh: NotifyChannelType;
  /** Có thì là sửa. */
  dangCo: NotifyChannel | null;
  onClose: () => void;
}) {
  const { t } = useT();
  const dich = useDichCanhBao();
  const dichLoi = useLoi();
  const luu = useLuuKenh(pv);
  const chat = useChatTelegram();
  const [khoaLap] = useState(() => crypto.randomUUID());
  const [ten, setTen] = useState(dangCo?.name ?? '');
  const [bat, setBat] = useState(dangCo?.enabled ?? true);
  const [giaTri, setGiaTri] = useState<GiaTriForm>(() => giaTriBanDau(loaiKenh.fields, dangCo?.config));
  const [daBam, setDaBam] = useState(false);
  const [chats, setChats] = useState<TelegramChat[] | null>(null);
  const [loiChat, setLoiChat] = useState<string | null>(null);

  const kenh = loaiKenh.code;
  const secretsSet = dangCo?.secrets_set ?? [];
  const loi = kiemForm(loaiKenh.fields, giaTri, { kenh, secretsSet });
  const loiTen = ten.trim() === '' ? t('canhBaoKenh.loiTen') : undefined;
  const hopLe = !loiTen && Object.keys(loi).length === 0;
  const tenKenh = dich.tenKenh(kenh);

  const dat = (key: string, v: GiaTriForm[string]) => setGiaTri((g) => ({ ...g, [key]: v }));

  const gui = () => {
    setDaBam(true);
    if (!hopLe) return;
    const config = dongGoiCauHinh(loaiKenh.fields, giaTri, kenh);
    const lam = dangCo
      ? luu.mutateAsync({
          tao: false,
          kenh: dangCo,
          du: { name: ten.trim(), config, enabled: bat },
        })
      : luu.mutateAsync({
          tao: true,
          du: { channel_type: kenh, name: ten.trim(), config, enabled: bat },
          khoaLap,
        });
    lam.then(onClose).catch(() => undefined);
  };

  const layChat = () => {
    const token = String(giaTri.bot_token ?? '').trim();
    if (!token && !dangCo?.secrets_set.includes('bot_token')) {
      setLoiChat(t('canhBaoKenh.canToken'));
      return;
    }
    setLoiChat(null);
    chat.mutate(token ? { bot_token: token } : { channel_id: dangCo!.id }, {
      onSuccess: (ds) => {
        setChats(ds);
        if (ds.length === 0) setLoiChat(t('canhBaoKenh.khongThayChat'));
      },
      onError: (e) => setLoiChat(t('canhBaoKenh.loiChat', { loi: dichLoi(e) })),
    });
  };

  const phuCua = (key: string) => {
    if (kenh === 'TELEGRAM' && key === 'chat_id') {
      return (
        <div className="space-y-2">
          <Button variant="outline" size="sm" loading={chat.isPending} onClick={layChat}>
            {t('canhBaoKenh.layChatId')}
          </Button>
          {chats && chats.length > 0 && (
            <Select<string>
              className="w-full"
              aria-label={t('canhBaoKenh.chonChat')}
              placeholder={t('canhBaoKenh.chonChat')}
              onChange={(v) => dat('chat_id', v)}
              options={chats.map((c) => ({
                value: c.id,
                label: c.title ? `${c.title} · ${c.id}` : c.id,
              }))}
            />
          )}
          {loiChat && <p className="text-xs text-warning-700">{loiChat}</p>}
        </div>
      );
    }
    if (kenh === 'WEBHOOK' && key === 'body_template') {
      return (
        <p className="flex flex-wrap gap-1.5">
          {BIEN_WEBHOOK.map((b) => (
            <code key={b} className="rounded bg-subtle px-1.5 py-0.5 font-mono text-2xs text-content-secondary">
              {b}
            </code>
          ))}
        </p>
      );
    }
    return null;
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t(dangCo ? 'canhBaoKenh.hopSua' : 'canhBaoKenh.hopThem', {
        kenh: tenKenh,
      })}
      width={560}
      destroyOnHidden
      footer={
        <div className="flex flex-wrap justify-end gap-3">
          <Button variant="outline" size="md" onClick={onClose}>
            {t('chung.huy')}
          </Button>
          <Button variant="primary" size="md" loading={luu.isPending} onClick={gui}>
            {luu.isPending ? t('canhBao.dangLuu') : t(dangCo ? 'canhBaoKenh.nutLuu' : 'canhBaoKenh.nutThem')}
          </Button>
        </div>
      }
    >
      <form
        className="space-y-4 pt-2"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          gui();
        }}
      >
        <Field
          label={t('canhBaoKenh.truongTen')}
          required
          hint={t('canhBaoKenh.tenGoiY')}
          error={daBam ? loiTen : undefined}
        >
          {(a11y) => <Input {...a11y} maxLength={100} value={ten} onChange={(e) => setTen(e.target.value)} />}
        </Field>

        {loaiKenh.fields
          .filter((f) => !oBiAn(kenh, f.key, giaTri))
          .map((f) => (
            <OTruongCoNhan
              key={f.key}
              kenh={kenh}
              truong={f}
              giaTri={giaTri[f.key] ?? null}
              onChange={(v) => dat(f.key, v)}
              loi={daBam ? loi[f.key] : undefined}
              daLuuSecret={secretsSet.includes(f.key)}
              phu={phuCua(f.key)}
            />
          ))}

        <label className="flex items-center gap-3">
          <Switch checked={bat} onChange={setBat} aria-label={t('canhBaoKenh.batCauHinh')} />
          <span className="text-sm text-content-primary">{t('canhBaoKenh.batCauHinh')}</span>
        </label>

        {luu.error && <LoiTrongHop>{dichLoi(luu.error)}</LoiTrongHop>}
      </form>
    </Dialog>
  );
}

function OTruongCoNhan({
  kenh,
  truong,
  ...con
}: {
  kenh: MaKenh;
  truong: NotifyField;
  giaTri: GiaTriO;
  onChange: (v: GiaTriO) => void;
  loi?: LoiTruong;
  daLuuSecret?: boolean;
  phu?: ReactNode;
}) {
  const dich = useDichCanhBao();
  return (
    <OTruong
      truong={truong}
      nhan={dich.nhanTruong(kenh, truong.key)}
      goiY={dich.goiYTruong(kenh, truong.key)}
      {...con}
    />
  );
}
